import {
  leadsContractsListOptions,
  opportunitiesContractsListOptions,
  ReturnContractReadable
} from '@crm/api'
import { ContractSignedStatus } from '@crm/components'
import { OpportunityContext } from '@crm/contexts/OpportunityContext'
import { useAppNavigate } from '@crm/hooks'
import { paths } from '@crm/routes'
import {
  ConditionalText,
  DateTimeCell,
  DayLabel,
  TableActions,
  TableContainer
} from '@shared/components'
import { useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Flex, TableColumnsType, Tooltip, Typography } from 'antd'
import { MenuProps } from 'antd/lib'
import { Edit2, Refresh, Trash } from 'iconsax-react'
import { useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AddEditContract } from './AddEditContract'
import { ContractsFilter } from './ContractsFilter'
import { DeleteContract } from './DeleteContract'
import { ActionsType } from './types'
import { useFetchSiAsig } from './useFetchSiAsig'

type ModalType = 'edit' | 'delete'

const modalTypes: Record<ModalType, React.ComponentType<ActionsType>> = {
  edit: AddEditContract,
  delete: DeleteContract
}

export const ContractsCol = () => {
  const { t } = useLocales()
  const { id, opportunityId } = useParams()
  const [modalState, setModalState] = useState<{
    type: ModalType | null
    contract: ReturnContractReadable | null
  }>({ contract: null, type: null })
  const { type } = useContext(OpportunityContext)

  const navigate = useAppNavigate()
  const params = useQueryParams()

  const { mutate: fetchSiAsig, isPending: isFetchingSiAsig } = useFetchSiAsig()

  const { data: leadData, isLoading: isLeadLoading } = useQuery({
    ...leadsContractsListOptions({
      path: { lead_id: String(id) },
      query: { ...params }
    }),
    enabled: !!id && type === 'lead'
  })

  const { data: opportunityData, isLoading: isOpportunityLoading } = useQuery({
    ...opportunitiesContractsListOptions({
      path: { opportunity_id: String(opportunityId) },
      query: { ...params }
    }),
    enabled: !!opportunityId && type === 'opportunity'
  })

  const data = leadData || opportunityData
  const isLoading = isLeadLoading || isOpportunityLoading

  const actionItems = (
    contract: ReturnContractReadable
  ): MenuProps['items'] => {
    // Synced contracts come from SI ASIG and are read-only — edit/delete are
    // disabled with a tooltip instead of being hidden
    const isSynced = !!contract.synced_at

    // When synced, render the icon inside a full-width tooltip-wrapped label so
    // hovering anywhere on the menu item (not just the text) shows the info
    const readonlyLabel = (label: string, icon: React.ReactNode) =>
      isSynced ? (
        <Tooltip title={t('contracts.synced_readonly')} placement="left">
          <Flex align="center" gap={8} style={{ width: '100%' }}>
            {icon}
            <span>{label}</span>
          </Flex>
        </Tooltip>
      ) : (
        label
      )

    return [
      {
        label: readonlyLabel(t('buttons.edit'), <Edit2 size={20} />),
        key: 'edit',
        icon: isSynced ? undefined : <Edit2 size={20} />,
        disabled: isSynced,
        onClick: () => setModalState({ contract, type: 'edit' })
      },
      {
        label: t('contracts.fetch_si_asig'),
        key: 'fetch-si-asig',
        icon: <Refresh size={20} />,
        disabled: isFetchingSiAsig,
        onClick: () => contract.id && fetchSiAsig(contract.id)
      },
      {
        type: 'divider'
      },
      {
        label: readonlyLabel(t('buttons.delete'), <Trash size={20} />),
        key: 'delete',
        danger: true,
        icon: isSynced ? undefined : <Trash size={20} />,
        disabled: isSynced,
        onClick: () => setModalState({ contract, type: 'delete' })
      }
    ]
  }

  const columns: TableColumnsType<ReturnContractReadable> = [
    {
      title: t('contracts.contract_number'),
      dataIndex: 'number',
      key: 'number'
    },
    {
      title: t('contracts.value'),
      dataIndex: 'value',
      key: 'value'
    },
    {
      title: t('contracts.currency'),
      dataIndex: ['currency', 'name'],
      key: 'currency_name'
    },
    {
      title: t('task.owner'),
      dataIndex: ['owner', 'name'],
      key: 'owner_name'
    },
    {
      title: t('contracts.opportunity'),
      dataIndex: ['opportunity', 'id'],
      key: 'opportunity_name',
      render: (id, record) =>
        id ? (
          <Typography.Link
            onClick={() =>
              navigate(
                paths.opportunitiesDetails.root(
                  String(id),
                  String(
                    record?.opportunity?.lead || record?.opportunity?.client
                  )
                )
              )
            }
          >
            {id}
          </Typography.Link>
        ) : (
          '-'
        )
    },
    {
      title: t('contracts.product'),
      dataIndex: ['product', 'id'],
      key: 'products_name',
      render: (_, { product }) => (
        <ConditionalText condition={!!product?.name}>
          {product?.name}
        </ConditionalText>
      )
    },
    {
      title: t('contracts.registration_number'),
      dataIndex: ['vehicle', 'registration_number'],
      key: 'registration_number',
      render: (registrationNumber) => (
        <ConditionalText condition={!!registrationNumber}>
          {registrationNumber}
        </ConditionalText>
      )
    },
    {
      title: t('contracts.vehicle'),
      key: 'vehicle',
      render: (_, { vehicle }) => {
        const label = [vehicle?.brand, vehicle?.model]
          .filter(Boolean)
          .join(' / ')

        return <ConditionalText condition={!!label}>{label}</ConditionalText>
      }
    },
    {
      title: t('contracts.valid_from'),
      dataIndex: ['valid_from'],
      key: 'valid_from',
      render: (validFrom) => <DayLabel date={validFrom} />
    },
    {
      title: t('contracts.valid_to'),
      dataIndex: ['valid_to'],
      key: 'valid_to',
      render: (validTo) => <DayLabel date={validTo} />
    },
    {
      title: t('contracts.status'),
      dataIndex: ['status'],
      key: 'status',
      render: (_, contract) => <ContractSignedStatus contract={contract} />
    },
    {
      title: t('contracts.signed_at'),
      dataIndex: ['signed_at'],
      key: 'signed_at',
      render: (signedAt) => <DayLabel date={signedAt} />
    },
    {
      title: t('contracts.synced_at'),
      dataIndex: ['synced_at'],
      key: 'synced_at',
      render: (syncedAt) => <DateTimeCell date={syncedAt} />
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, contract) => <TableActions options={actionItems(contract)} />
    }
  ]

  const actionComponent = useMemo(() => {
    const Component = modalState.type ? modalTypes[modalState.type] : null

    return Component && modalState.contract ? (
      <Component
        open
        contract={modalState.contract}
        onCancel={() => setModalState({ contract: null, type: null })}
      />
    ) : null
  }, [modalState])

  return (
    <Flex vertical gap={16}>
      <TableContainer
        rowKey="id"
        columns={columns}
        data={data}
        loading={isLoading}
        leftChildren={<ContractsFilter />}
      />

      {actionComponent}
    </Flex>
  )
}
